import React, { useState } from 'react';
import { Search, Plus, Download, Filter, MapPin, Calendar, Image as ImageIcon, FileText, Eye, Edit, TrendingUp, AlertCircle } from 'lucide-react';

const DemoInstitution = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const artworks = [
    {
      id: 1,
      title: "Portrait de femme",
      artist: "Anonyme, École française",
      year: "XVIIIe siècle",
      medium: "Huile sur toile",
      dimensions: "116 × 89 cm",
      location: "Salon Rouge - Mur Est",
      status: "Exposé",
      condition: "Bon état général",
      image: "https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=300&h=400&fit=crop",
      inventory: "INV-2024-001",
      value: "Estimation: 15 000 - 20 000 €",
      lastCheck: "15/11/2025",
      type: "painting"
    },
    {
      id: 2,
      title: "Buste d'homme au manteau",
      artist: "Atelier de Jean-Baptiste Carpeaux",
      year: "circa 1870",
      medium: "Sculpture - Terre cuite",
      dimensions: "45 × 32 × 28 cm",
      location: "Galerie des Sculptures",
      status: "Réserve",
      condition: "Fissure mineure à surveiller",
      image: "https://images.unsplash.com/photo-1578926288207-a90a5366759d?w=300&h=400&fit=crop",
      inventory: "INV-2024-002",
      value: "Estimation: 8 000 - 12 000 €",
      lastCheck: "20/11/2025",
      type: "sculpture"
    },
    {
      id: 3,
      title: "Paysage de campagne",
      artist: "École de Barbizon",
      year: "circa 1850-1860",
      medium: "Huile sur panneau",
      dimensions: "32 × 41 cm",
      location: "Bureau - Étage 1",
      status: "Prêt (retour prévu 01/2026)",
      condition: "Excellent état",
      image: "https://images.unsplash.com/photo-1579541814924-49fef17c5be5?w=300&h=400&fit=crop",
      inventory: "INV-2024-003",
      value: "Estimation: 5 000 - 7 000 €",
      lastCheck: "28/10/2025",
      type: "painting"
    },
    {
      id: 4,
      title: "Nature morte aux fruits",
      artist: "École flamande",
      year: "XVIIe siècle",
      medium: "Huile sur toile",
      dimensions: "65 × 81 cm",
      location: "Salle à manger",
      status: "Exposé",
      condition: "Restauration recommandée",
      image: "https://images.unsplash.com/photo-1577083552431-6e5fd01988ec?w=300&h=400&fit=crop",
      inventory: "INV-2024-004",
      value: "Estimation: 25 000 - 35 000 €",
      lastCheck: "10/11/2025",
      type: "painting"
    },
    {
      id: 5,
      title: "Étude de nu féminin",
      artist: "Atelier parisien",
      year: "circa 1920",
      medium: "Dessin - Fusain sur papier",
      dimensions: "42 × 29,7 cm",
      location: "Cabinet des Arts Graphiques",
      status: "Réserve",
      condition: "Bon état général",
      image: "https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=300&h=400&fit=crop",
      inventory: "INV-2024-005",
      value: "Estimation: 1 500 - 2 500 €",
      lastCheck: "05/11/2025",
      type: "drawing"
    },
    {
      id: 6,
      title: "Scène mythologique",
      artist: "D'après Poussin",
      year: "XIXe siècle",
      medium: "Huile sur toile",
      dimensions: "195 × 145 cm",
      location: "Grand Salon",
      status: "Exposé",
      condition: "Excellent état",
      image: "https://images.unsplash.com/photo-1578301978018-3005759f48f7?w=300&h=400&fit=crop",
      inventory: "INV-2024-006",
      value: "Estimation: 40 000 - 60 000 €",
      lastCheck: "12/11/2025",
      type: "painting"
    }
  ];

  const stats = [
    { label: "Œuvres inventoriées", value: "847", trend: "+12 ce mois" },
    { label: "Expositions en cours", value: "3", trend: "2 temporaires" },
    { label: "Prêts actifs", value: "8", trend: "4 retours prévus" },
    { label: "Valeur totale estimée", value: "12,4 M€", trend: "+8% vs 2024" }
  ];

  const filteredArtworks = activeFilter === 'all' 
    ? artworks 
    : artworks.filter(a => a.type === activeFilter);

  const getTypeCount = (type) => {
    return artworks.filter(a => a.type === type).length;
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#FAFAF8',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
    }}>
      {/* Demo Banner */}
      <div style={{
        backgroundColor: '#4F46E5',
        color: 'white',
        padding: '12px 0',
        textAlign: 'center'
      }}>
        <p style={{ fontSize: '14px', fontWeight: 500, margin: 0 }}>
          📍 Démo interactive • Interface Institutions & Musées • 
          <a href="mailto:contact@artkiv.com" style={{ color: 'white', textDecoration: 'underline', marginLeft: '8px' }}>
            Demander un accès beta
          </a>
        </p>
      </div>

      {/* Header */}
      <header style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #E5E7EB',
        padding: '16px 0'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '0 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div>
              <h1 style={{ 
                fontSize: '24px', 
                fontFamily: 'Georgia, serif', 
                color: '#111827',
                margin: 0 
              }}>
                Collection Institution
              </h1>
              <p style={{ fontSize: '14px', color: '#6B7280', margin: '4px 0 0' }}>
                Gestion patrimoniale professionnelle
              </p>
            </div>
            <span style={{
              padding: '6px 12px',
              backgroundColor: '#EEF2FF',
              color: '#4F46E5',
              borderRadius: '9999px',
              fontSize: '14px',
              fontWeight: 500
            }}>
              Musée & Château
            </span>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button style={{
              padding: '8px 16px',
              color: '#374151',
              backgroundColor: 'transparent',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background-color 0.2s'
            }}>
              <Download size={16} />
              <span>Exporter</span>
            </button>
            <button style={{
              padding: '8px 16px',
              backgroundColor: '#4F46E5',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background-color 0.2s'
            }}>
              <Plus size={16} />
              <span>Nouvelle œuvre</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        padding: '32px 24px'
      }}>
        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '16px',
          marginBottom: '32px'
        }}>
          {stats.map((stat, index) => (
            <div key={index} style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              padding: '20px',
              transition: 'box-shadow 0.2s'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'start',
                marginBottom: '8px'
              }}>
                <p style={{ fontSize: '14px', color: '#6B7280', fontWeight: 500, margin: 0 }}>
                  {stat.label}
                </p>
                <TrendingUp size={16} color="#10B981" />
              </div>
              <p style={{ 
                fontSize: '30px', 
                fontFamily: 'Georgia, serif', 
                color: '#111827',
                margin: '4px 0'
              }}>
                {stat.value}
              </p>
              <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>
                {stat.trend}
              </p>
            </div>
          ))}
        </div>

        {/* Search & Filters */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #E5E7EB',
          padding: '16px',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '12px',
            flexWrap: 'wrap'
          }}>
            <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
              <Search 
                size={20} 
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9CA3AF'
                }} 
              />
              <input
                type="text"
                placeholder="Rechercher par titre, artiste, numéro d'inventaire..."
                style={{
                  width: '100%',
                  padding: '8px 16px 8px 40px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <button style={{
              padding: '8px 16px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              backgroundColor: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Filter size={16} />
              Filtres
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <button
              onClick={() => setActiveFilter('all')}
              style={{
                padding: '6px 12px',
                backgroundColor: activeFilter === 'all' ? '#E0E7FF' : '#F3F4F6',
                color: activeFilter === 'all' ? '#4338CA' : '#374151',
                border: 'none',
                borderRadius: '9999px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
            >
              Toutes les œuvres (847)
            </button>
            <button
              onClick={() => setActiveFilter('painting')}
              style={{
                padding: '6px 12px',
                backgroundColor: activeFilter === 'painting' ? '#E0E7FF' : '#F3F4F6',
                color: activeFilter === 'painting' ? '#4338CA' : '#374151',
                border: 'none',
                borderRadius: '9999px',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
            >
              Peintures ({getTypeCount('painting')})
            </button>
            <button
              onClick={() => setActiveFilter('sculpture')}
              style={{
                padding: '6px 12px',
                backgroundColor: activeFilter === 'sculpture' ? '#E0E7FF' : '#F3F4F6',
                color: activeFilter === 'sculpture' ? '#4338CA' : '#374151',
                border: 'none',
                borderRadius: '9999px',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
            >
              Sculptures ({getTypeCount('sculpture')})
            </button>
            <button
              onClick={() => setActiveFilter('drawing')}
              style={{
                padding: '6px 12px',
                backgroundColor: activeFilter === 'drawing' ? '#E0E7FF' : '#F3F4F6',
                color: activeFilter === 'drawing' ? '#4338CA' : '#374151',
                border: 'none',
                borderRadius: '9999px',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
            >
              Dessins ({getTypeCount('drawing')})
            </button>
          </div>
        </div>

        {/* Artwork Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(500px, 1fr))',
          gap: '24px',
          marginBottom: '32px'
        }}>
          {filteredArtworks.map((artwork) => (
            <div key={artwork.id} style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              overflow: 'hidden',
              transition: 'box-shadow 0.3s'
            }}>
              <div style={{ display: 'flex', gap: '16px', padding: '20px' }}>
                <div style={{
                  width: '128px',
                  height: '160px',
                  flexShrink: 0,
                  backgroundColor: '#F3F4F6',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <img 
                    src={artwork.image} 
                    alt={artwork.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                    marginBottom: '8px',
                    gap: '8px'
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontFamily: 'Georgia, serif',
                        color: '#111827',
                        margin: '0 0 4px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {artwork.title}
                      </h3>
                      <p style={{
                        fontSize: '14px',
                        color: '#6B7280',
                        margin: '0 0 4px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {artwork.artist}
                      </p>
                      <p style={{ fontSize: '12px', color: '#9CA3AF', margin: 0 }}>
                        {artwork.year}
                      </p>
                    </div>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 500,
                      flexShrink: 0,
                      backgroundColor: artwork.status === 'Exposé' 
                        ? '#D1FAE5' 
                        : artwork.status === 'Réserve'
                        ? '#F3F4F6'
                        : '#DBEAFE',
                      color: artwork.status === 'Exposé'
                        ? '#065F46'
                        : artwork.status === 'Réserve'
                        ? '#374151'
                        : '#1E40AF'
                    }}>
                      {artwork.status}
                    </span>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      color: '#6B7280',
                      marginBottom: '6px'
                    }}>
                      <FileText size={14} />
                      <span>{artwork.medium}</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      color: '#6B7280',
                      marginBottom: '6px'
                    }}>
                      <ImageIcon size={14} />
                      <span>{artwork.dimensions}</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      color: '#6B7280',
                      marginBottom: '6px'
                    }}>
                      <MapPin size={14} />
                      <span style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {artwork.location}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      color: '#6B7280'
                    }}>
                      <Calendar size={14} />
                      <span>Contrôle: {artwork.lastCheck}</span>
                    </div>
                  </div>

                  {artwork.condition !== "Excellent état" && artwork.condition !== "Bon état général" && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      color: '#B45309',
                      backgroundColor: '#FEF3C7',
                      borderRadius: '4px',
                      padding: '6px 8px',
                      marginBottom: '12px'
                    }}>
                      <AlertCircle size={14} />
                      <span style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {artwork.condition}
                      </span>
                    </div>
                  )}

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '12px',
                    borderTop: '1px solid #F3F4F6'
                  }}>
                    <div style={{ minWidth: 0, flex: 1, marginRight: '8px' }}>
                      <p style={{ fontSize: '12px', color: '#9CA3AF', margin: '0 0 4px' }}>
                        N° {artwork.inventory}
                      </p>
                      <p style={{
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#374151',
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {artwork.value}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                      <button style={{
                        padding: '8px',
                        color: '#6B7280',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}>
                        <Eye size={16} />
                      </button>
                      <button style={{
                        padding: '8px',
                        color: '#6B7280',
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}>
                        <Edit size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #E5E7EB',
          padding: '48px 32px',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontFamily: 'Georgia, serif',
            color: '#111827',
            marginBottom: '12px'
          }}>
            Interface conçue pour les institutions patrimoniales
          </h2>
          <p style={{
            color: '#6B7280',
            marginBottom: '24px',
            maxWidth: '700px',
            margin: '0 auto 24px'
          }}>
            Gestion complète de vos collections avec inventaire, mouvements, expositions, valorisation et rapports professionnels. Solution européenne, conforme RGPD.
          </p>
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <a 
              href="mailto:contact@artkiv.com?subject=Demande accès beta - Institution"
              style={{
                padding: '12px 24px',
                backgroundColor: '#4F46E5',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 500,
                display: 'inline-block'
              }}
            >
              Demander un accès beta
            </a>
            <a 
              href="/"
              style={{
                padding: '12px 24px',
                border: '1px solid #D1D5DB',
                color: '#374151',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 500,
                display: 'inline-block'
              }}
            >
              Retour à l'accueil
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        backgroundColor: 'white',
        borderTop: '1px solid #E5E7EB',
        marginTop: '48px'
      }}>
        <div style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '24px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
            fontSize: '14px',
            color: '#6B7280'
          }}>
            <p style={{ margin: 0 }}>© 2025 Artkiv. Gestion patrimoniale des collections d'art.</p>
            <div style={{ display: 'flex', gap: '24px' }}>
              <a href="mailto:contact@artkiv.com" style={{ color: '#6B7280', textDecoration: 'none' }}>
                contact@artkiv.com
              </a>
              <a href="https://artkiv.com" style={{ color: '#6B7280', textDecoration: 'none' }}>
                artkiv.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DemoInstitution;